<script setup lang="ts">
import type { RewardsSection } from '~/composables/useRewardsDrawer'
import { loadRewardsMessages } from '@layers/i18n/app/utils/rewardsMessages'

const drawer = useRewardsDrawer()
const rewards = useRewards()
const route = useRoute()
const router = useRouter()
const i18n = useI18n()
const analytics = useRewardAnalytics()
useRewardLifecycleAnalytics()
const messagesLocale = ref('')
const messagesReady = computed(() => messagesLocale.value === i18n.locale.value)
watch([drawer.wanted, rewards.claimResult, i18n.locale], async () => {
  if (!drawer.wanted.value && !rewards.claimResult.value)
    return
  const locale = i18n.locale.value
  await loadRewardsMessages(i18n)
  if (i18n.locale.value === locale)
    messagesLocale.value = locale
}, { immediate: true })

// Handle navigation and auth returns together: a login intent must win over
// closing the drawer for the underlying page/session change in the same tick.
watch([() => route.path, () => route.query.rewards, rewards.scope, i18n.locale, rewards.customerId], ([path, section, scope, locale], previous) => {
  const [previousPath, , previousScope, previousLocale, previousCustomer] = previous
  if ((previousPath && path !== previousPath)
    || (previousLocale && locale !== previousLocale)
    || (previousCustomer && scope !== previousScope)) {
    drawer.reset()
  }

  if (typeof section !== 'string')
    return
  const allowed: RewardsSection[] = ['overview', 'missions', 'activity', 'levels', 'rules', 'profile', 'invite', 'notifications', 'reviews']
  if (!allowed.includes(section as RewardsSection))
    return
  drawer.open(section as RewardsSection)
  const { rewards: _, ...query } = route.query
  void router.replace({ path, query, hash: route.hash })
}, { immediate: true })

watch(drawer.visible, (visible, previous) => {
  if (visible && !previous)
    analytics.track('coins_wallet_opened')
}, { immediate: true })
watch(rewards.claimResult, (claim) => {
  if (claim?.success && claim.coins > 0)
    analytics.track('reward_claimed', { coins: claim.coins, pending: claim.pending, level: claim.wallet?.level ?? rewards.wallet.value?.level ?? 'SILVER' })
}, { immediate: true })
</script>

<template>
  <LazyRewardsDrawer v-if="drawer.wanted.value && messagesReady" />
  <LazyRewardsCelebration v-if="rewards.claimResult.value && messagesReady" />
</template>
